import React, { useState } from 'react';
import Grid from '../../../../muiComponents/Grid';
import {
    creditCardColumnGrid,
    EMPTY_CREDIT_CARD_EXPIRE_DATE,
    EMPTY_CREDIT_CARD_HOLDER_NAME,
    EMPTY_CREDIT_CARD_NUMBER,
    gridColumnsSpacing,
    gridRowsSpacing,
    singleColumnGrid,
} from '../../../../utils/bookings/bookingUtils';
import GridChild from '../../../../muiComponents/GridChild';
import front from '../../../../images/credit-card-front.svg';
import back from '../../../../images/credit-card-back.svg';
import Card from '../../../../muiComponents/Card';
import {
    BookingRatesState,
    CancellationPolicyState,
    CreditCardState,
} from '../../../../types/booking/BookingTypes';

import IconButton from '../../../../muiComponents/IconButton';
import { formatCreditCardExpireDate } from '../../../../utils/formatDates';
import Icon from '../../../../muiComponents/Icon';
import useUserProfile from '../../../../customHooks/useUserProfile';
import Title from '../../../components/Title';
import SectionTitle from '../../../components/SectionTitle/SectionTitle';
import MainSectionTitle from '../../yourProperties/Title';

import {
    formatCreditCardNumber,
    formatNumber,
    maskCreditCardNumber,
} from '../../../../utils/usefulFunctions';

interface FinanceProps {
    paymentId: number;
    creditCard: CreditCardState;
    totalAmount: number;
    currency: string;
    cancellationPolicies: CancellationPolicyState[];
    prices: BookingRatesState[];
}

const Finance = (props: FinanceProps) => {
    const {
        creditCard,
        paymentId,
        totalAmount,
        currency,
        cancellationPolicies,
        prices,
    } = props;
    const {
        creditCardNumber,
        creditCardExpiryDate,
        creditCardHolderName,
        cvc,
    } = creditCard;
    const cardNumberToPaint = creditCardNumber || EMPTY_CREDIT_CARD_NUMBER;
    const { translate } = useUserProfile();

    const [isCardDataVisible, setIsCardDataVisible] = useState(false);

    return (
        <section className='booking__section'>
            <MainSectionTitle text={translate('payment_info')} />
            <Card className='booking__card padding20 flex-column gap-20'>
                <div>
                    {prices.map(price => (
                        <div key={price.id} className='flex-column gap-10'>
                            <SectionTitle
                                title={translate('price') + ': ' + price.room}
                                className='text-capitalize text-secunday700 padding-inline15'
                            />
                            <Grid
                                style={{ width: 'auto', margin: 0 }}
                                columnSpacing={gridColumnsSpacing}
                                rowSpacing={gridRowsSpacing}
                            >
                                {price.rates.map(rate => (
                                    <GridChild
                                        key={rate.id}
                                        gridProps={singleColumnGrid}
                                    >
                                        <div className='flex-column gap-5'>
                                            <div className='flex-align-center gap-5 text-overflow-ellipsis'>
                                                <Icon
                                                    iconName='Sell'
                                                    className='text-secunday700'
                                                />
                                                <Title className='text-capitalize'>
                                                    {translate('night') +
                                                        ' #' +
                                                        rate.night}
                                                </Title>
                                            </div>
                                            <p>
                                                {currency
                                                    ? formatNumber(
                                                          rate.amount,
                                                          'currency',
                                                          currency
                                                      )
                                                    : formatNumber(
                                                          rate.amount,
                                                          'decimal'
                                                      )}
                                            </p>
                                        </div>
                                    </GridChild>
                                ))}
                            </Grid>
                        </div>
                    ))}

                    <div className='flex-column gap-5 flex-align-end padding-inline15'>
                        <div
                            style={{
                                width: '100%',
                                borderBottom: '1px solid #eee',
                            }}
                        />
                        <div className='flex-column gap-5'>
                            <div className='flex-align-center gap-5 text-overflow-ellipsis'>
                                <Icon
                                    iconName='Sell'
                                    className='text-secunday700'
                                />
                                <Title className='text-capitalize'>
                                    {translate('total')}
                                </Title>
                            </div>
                            <p>
                                {currency
                                    ? formatNumber(
                                          totalAmount,
                                          'currency',
                                          currency
                                      )
                                    : formatNumber(totalAmount, 'decimal')}
                            </p>
                        </div>
                    </div>
                </div>
            </Card>
            {paymentId > 0 ? (
                <Card className='booking__card padding20 flex-column gap-20'>
                    <>
                        <div className='flex flex-between flex-align-center gap-10 padding-inline15'>
                            <SectionTitle
                                title='Payment'
                                className='text-capitalize text-secunday700'
                            />
                            <IconButton
                                iconName={
                                    isCardDataVisible
                                        ? 'VisibilityOff'
                                        : 'Visibility'
                                }
                                onClick={() =>
                                    setIsCardDataVisible(prev => !prev)
                                }
                            />
                        </div>
                        <Grid
                            style={{ width: 'auto', margin: 0 }}
                            columnSpacing={gridColumnsSpacing}
                            rowSpacing={gridRowsSpacing}
                        >
                            {/* <CreditCard /> */}
                            <GridChild gridProps={creditCardColumnGrid}>
                                <div
                                    className='width100perc'
                                    style={{
                                        position: 'relative',
                                    }}
                                >
                                    <p className='booking-credit-card__number'>
                                        {isCardDataVisible
                                            ? formatCreditCardNumber(
                                                  cardNumberToPaint
                                              )
                                            : maskCreditCardNumber(
                                                  cardNumberToPaint
                                              )}
                                    </p>

                                    {isCardDataVisible ? (
                                        <>
                                            <p className='booking-credit-card__holder-name'>
                                                {creditCardHolderName ||
                                                    EMPTY_CREDIT_CARD_HOLDER_NAME}
                                            </p>
                                            <p className='booking-credit-card__expire-date'>
                                                {formatCreditCardExpireDate(
                                                    creditCardExpiryDate
                                                ) ||
                                                    EMPTY_CREDIT_CARD_EXPIRE_DATE}
                                            </p>
                                        </>
                                    ) : (
                                        ''
                                    )}
                                    <img className='width100perc' src={front} />
                                </div>
                            </GridChild>
                            <GridChild gridProps={creditCardColumnGrid}>
                                <div
                                    className='width100perc'
                                    style={{
                                        position: 'relative',
                                    }}
                                >
                                    <p className='booking-credit-card__cvc'>
                                        {isCardDataVisible
                                            ? cvc || 'XXX'
                                            : '***'}
                                    </p>
                                    <img className='width100perc' src={back} />
                                </div>
                            </GridChild>
                        </Grid>
                    </>
                </Card>
            ) : (
                ''
            )}
            {cancellationPolicies.length ? (
                <Card className='booking__card padding35 flex-column gap-20'>
                    <SectionTitle
                        className='text-capitalize text-secunday700'
                        title={translate('cancellation_policy')}
                    />
                    <div className='flex-column gap-5'>
                        {cancellationPolicies?.map(policy => {
                            return (
                                <p key={policy.id}>{policy.text || '----'}</p>
                            );
                        })}
                    </div>
                </Card>
            ) : (
                ''
            )}
        </section>
    );
};

export default Finance;
